/**
 * Single source of truth for DTS (sweepstakes) compliance copy and constants.
 *
 * Keeping these here means the legally-sensitive wording lives in ONE place and
 * cannot drift between the footer, legal pages, registration screen, and storefront.
 * Placeholder legal body text is CMS-driven and swapped for counsel's final copy
 * later with zero code change; the items below are structural/boilerplate.
 */

/**
 * COIN NAMING HAS TWO LAYERS. Do not confuse them.
 *
 *   1. DISPLAY NAMES (this file) — the words a user reads: "Dream Cash",
 *      "Gold Coins". These are admin-configurable from the site settings panel
 *      (Sweepstakes compliance section) and may be rebranded at any time
 *      without a deploy. Always read them through getSweepsCoinName() /
 *      getGoldCoinName(); never hardcode a coin name at a call site.
 *
 *   2. SCHEMA FIELD NAMES (backend/Models/userWallet.js) — the MongoDB keys
 *      gc_balance / unplayed_dc / freebet_dc / sticky_dc / redeemable_dc.
 *      These are NEVER configurable. Indexes, the migration script, and every
 *      dualCoinLedger aggregation bind to those literals; making them settable
 *      would break the ledger. They stay `_dc` regardless of what the coin is
 *      called on screen, and a user never sees them.
 *
 * Renaming the coin is a layer-1 change only.
 */

/**
 * Canonical name of the free, redeemable promotional coin (the "sweeps" coin).
 *
 * Every user-facing reference to the redeemable coin must use this constant —
 * via getSweepsCoinName(), so an admin override wins — so the name lives in
 * exactly one place. "Dream Cash" is the launch default agreed with the client
 * and matches the wording already used in backend messages; an admin rebrand is
 * a site-settings edit, not a code change.
 */
export const SWEEPS_COIN_NAME = "Dream Cash";

/**
 * Canonical name of the utility play coin — what play amounts are denominated
 * in on DTS. No cash value, not redeemable. Used to relabel the "$" play UI
 * (risk/win/return/balance) so it reads as coins instead of real money.
 */
export const GOLD_COIN_NAME = "Gold Coins";

/**
 * Admin-configurable compliance values, sourced from site settings.
 *
 * These live under one runtime cache instead of being hardcoded, so the client
 * can set the legal entity, AMOE address, excluded states, coin names, and
 * minimum age from the admin panel without a code change.
 * `setComplianceConfig()` is called once when site settings load (see
 * components/actions/siteSettings.js), mirroring the existing `setWagerConfig`
 * pattern. Every getter falls back to the code default below when the admin
 * value is blank, so the site is never broken.
 */
let _complianceCfg = {};

/**
 * Site settings load asynchronously, well AFTER the app has mounted and drawn
 * its first screen. Nothing about a plain module variable tells React that the
 * vocabulary changed, so any component that already rendered — or that captured
 * a label into useState at mount — keeps showing the built-in defaults forever.
 *
 * The version counter plus listener set make the config an external store React
 * can subscribe to (see useComplianceConfig below). The version is a primitive
 * that only changes on a real update, which is what useSyncExternalStore needs
 * to avoid an infinite render loop.
 */
let _complianceCfgVersion = 0;
const _complianceCfgListeners = new Set();

/** Populate the runtime compliance config from loaded site settings. */
export const setComplianceConfig = (settings = {}) => {
  _complianceCfg = settings && typeof settings === "object" ? settings : {};
  _complianceCfgVersion += 1;
  _complianceCfgListeners.forEach((listener) => listener());
};

/** Subscribe to compliance-config changes. Returns an unsubscribe function. */
export const subscribeComplianceConfig = (listener) => {
  _complianceCfgListeners.add(listener);
  return () => _complianceCfgListeners.delete(listener);
};

/** Snapshot of the current config generation. */
export const getComplianceConfigVersion = () => _complianceCfgVersion;

const cfgStr = (key) => {
  const v = _complianceCfg[key];
  return typeof v === "string" ? v.trim() : "";
};

/** Effective redeemable-coin name (admin value, else the "Sweeps Coins" default). */
export const getSweepsCoinName = () => cfgStr("sweepsCoinName") || SWEEPS_COIN_NAME;

/** Effective play-coin name for wagering displays (admin value, else the "Gold Coins" default). */
export const getGoldCoinName = () => cfgStr("goldCoinName") || GOLD_COIN_NAME;

export const getAmoePolicy = () => ({
  mailingAddress: cfgStr("amoeMailingAddress"),
  validityDays: Number(_complianceCfg.amoeValidityDays) > 0 ? Number(_complianceCfg.amoeValidityDays) : 30,
  creditAmount: Number(_complianceCfg.amoeCreditAmount) > 0 ? Number(_complianceCfg.amoeCreditAmount) : 5,
});

/**
 * Shipped default DTS terminology — the sweepstakes-safe replacements for the
 * words a sportsbook uses. Each is admin-overridable from the site settings
 * panel (Sweepstakes compliance) via `term<Key>Singular` / `term<Key>Plural`;
 * a blank override falls back to the default here, so the UI is never empty.
 *
 * These cover the forbidden vocabulary the compliance word-scan rejects:
 *   entry      <- bet / bets / wager / wagers
 *   play       <- betting / wagering (the activity)
 *   prize      <- payout / payouts
 *   redemption <- withdraw / withdrawal
 *   purchase   <- deposit / deposits
 *   earlySettlement <- cash out
 *
 * Early settlement is its own term rather than reusing "redemption": a
 * redemption turns prize coins into a real-world prize, while settling an entry
 * early only closes it at the live offer. Labelling both "Redemption" told
 * players an open entry was being paid out as a prize.
 *
 * Verb forms ("to withdraw funds") are NOT substituted word-for-word — the call
 * site is reworded to use the noun ("to request a redemption") instead. Adding
 * an inflection engine here would be far more fragile than rewording 40 strings.
 */
export const TERM_DEFAULTS = {
  entry: { one: "Entry", many: "Entries" },
  play: { one: "Play", many: "Plays" },
  prize: { one: "Prize", many: "Prizes" },
  redemption: { one: "Redemption", many: "Redemptions" },
  purchase: { one: "Purchase", many: "Purchases" },
  earlySettlement: { one: "Early Settlement", many: "Early Settlements" },
};

/** Site-setting key holding the admin override for a term, e.g. termEntryPlural. */
const termCfgKey = (key, plural) =>
  `term${key.charAt(0).toUpperCase()}${key.slice(1)}${plural ? "Plural" : "Singular"}`;

/**
 * Effective DTS term for `key` — the admin override when set, else the shipped
 * default. `plural` selects the plural form; `case` renders it "upper"/"lower"
 * (default: verbatim, as authored by the admin).
 *
 * An unrecognised key returns "" rather than throwing: a typo at one call site
 * must never blank the whole screen.
 */
export const getTerm = (key, { plural = false, case: casing } = {}) => {
  const def = TERM_DEFAULTS[key];
  if (!def) return "";
  const word = cfgStr(termCfgKey(key, plural)) || (plural ? def.many : def.one);
  if (casing === "upper") return word.toUpperCase();
  if (casing === "lower") return word.toLowerCase();
  return word;
};

/**
 * Display label for an entry-type tab ("Straight Bet", "Parlay Bet", "Teaser",
 * "Call Bets") and for stored order types ("Straight", "Parlay", "Teaser",
 * "Call").
 *
 * Those tab strings are NOT just labels — they are the tab's state value, and
 * roughly forty `betTabName === "Straight Bet"` comparisons across the bet
 * builder depend on them literally. Renaming the value would break tab
 * switching everywhere, so the identifier stays fixed and only what the user
 * reads is translated, at the single point where the tabs are rendered.
 *
 * `dts` is passed in rather than read here so this stays a pure function.
 * An unrecognised id passes through untouched, so adding a tab can never
 * blank it.
 */
const ENTRY_TYPE_LABELS = {
  "Straight Bet": () => "Single",
  Straight: () => "Single",
  "Parlay Bet": () => "Combo",
  Parlay: () => "Combo",
  Teaser: () => "+Points Combo",
  "Call Bets": () => "Target pick",
  Call: () => "Target pick",
  // Bare "Bet" is the fallback when no type was recorded.
  Bet: () => getTerm("entry"),
};

export const getEntryTypeLabel = (tabId, dts) => {
  if (!dts) return tabId;
  const build = ENTRY_TYPE_LABELS[tabId];
  return build ? build() : tabId;
};

/**
 * Featured board / slip market header. Odds API keys stay h2h / spreads /
 * totals; only the player-facing word changes on DTS (Moneyline → To Win).
 */
export const getFeaturedMarketLabel = (typeOrKey, dts) => {
  const key = String(typeOrKey || "").toLowerCase();
  if (key === "h2h" || key === "moneyline" || key === "m-line" || key === "money") {
    return dts ? "To Win" : "M-Line";
  }
  if (key === "totals" || key === "total") return "Total";
  if (key === "spreads" || key === "spread") return "Spread";
  return typeOrKey || "Pick";
};

/** Slip field / default dropdown label for half-point purchases. */
export const getBuyPointsLabel = (dts) => (dts ? "Adjust line" : "Buy Points");

/** Default select option text (trailing ! is the legacy PPH affordance). */
export const getBuyPointsDefaultOptionLabel = (dts) =>
  dts ? "Adjust line" : "Buy Points!";

/** One buy-ladder step label after the default. */
export const getBuyPointsStepLabel = (dts, stepAmount, priceText) =>
  dts
    ? `Adjust ${stepAmount} point ${priceText}`.trim()
    : `Buy ${stepAmount} point ${priceText}`.trim();

/** Every configurable term key, for the admin panel and validation. */
export const TERM_KEYS = Object.keys(TERM_DEFAULTS);

/**
 * The mandatory "No Purchase Necessary" disclaimer.
 * Exact wording required by U.S. promotional law — do not paraphrase.
 * Must appear on: Terms of Use, Official Rules, Registration, and Storefront.
 */
export const NO_PURCHASE_NECESSARY_TEXT =
  "NO PURCHASE OR PAYMENT OF ANY KIND IS NECESSARY TO ENTER OR WIN ANY " +
  "AMUSEMENT CONTEST. A PURCHASE DOES NOT IMPROVE YOUR CHANCE OF WINNING.";

/** The mandatory footer sweepstakes disclaimer (links to Official Rules in the UI). */
export const SWEEPSTAKES_FOOTER_DISCLAIMER = {
  before:
    "NO PURCHASE NECESSARY to enter the sweepstakes. SWEEPSTAKES ARE VOID WHERE PROHIBITED BY LAW. For detailed rules, see the ",
  linkLabel: "Official Sweepstakes Rules",
  linkSlug: "official-rules",
  after: ".",
};

/** Responsible-play resource shown at the foot of every page (URL/label to confirm with client). */
export const RESPONSIBLE_PLAY = {
  before: "DreamSports is committed to responsible social play. For more information visit ",
  href: "https://www.ncpgambling.org/",
  label: "ncpgambling.org",
};

/**
 * Legal links that must appear in the footer of every page (DTS mode).
 * Each opens a dedicated, publicly reachable /info/:slug page (never blank/gated).
 */
export const LEGAL_FOOTER_LINKS = [
  { slug: "terms", label: "Terms of Use" },
  { slug: "official-rules", label: "Official Sweepstakes Rules" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "amoe", label: "Sweepstakes Rules / Alternative Method of Entry" },
];

/**
 * CMS identifier for the admin-editable footer legal block (copyright line +
 * ownership line + sweepstakes disclaimers, everything below LEGAL_FOOTER_LINKS).
 * Admins create a page with this exact identifier in the Info Pages CMS to
 * override the hardcoded boilerplate below with their own formatted content —
 * same mechanism as Terms/Privacy/Official Rules. When no such CMS entry
 * exists, the footer falls back to the hardcoded copy so it is never blank.
 */
export const FOOTER_LEGAL_CMS_IDENTIFIER = "footer-legal-content";

/**
 * DEFAULT jurisdictions excluded from the sweepstakes (used when the admin has
 * not configured a list). Spec names WA / NV / ID / MI; the client sets counsel's
 * authoritative list from the admin panel (siteSetting.excludedStates).
 * Shared by signup gating and the geo gate.
 */
export const EXCLUDED_STATES = [
  { code: "WA", name: "Washington" },
  { code: "NV", name: "Nevada" },
  { code: "ID", name: "Idaho" },
  { code: "MI", name: "Michigan" },
];

export const EXCLUDED_STATE_CODES = EXCLUDED_STATES.map((s) => s.code);

/** US state code -> name, for resolving admin-supplied codes to display names. */
const US_STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
  ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
};

/**
 * Effective excluded-state codes: the admin-configured list when set, otherwise
 * the default WA/NV/ID/MI. Returns uppercase 2-letter codes.
 */
export const getExcludedStateCodes = () => {
  const admin = _complianceCfg.excludedStates;
  if (Array.isArray(admin) && admin.length) {
    return admin.map((s) => String(s || "").trim().toUpperCase()).filter(Boolean);
  }
  return EXCLUDED_STATE_CODES;
};

/** Effective excluded states as {code, name}, for display (e.g. the geo block). */
export const getExcludedStates = () =>
  getExcludedStateCodes().map((code) => ({
    code,
    name: US_STATE_NAMES[code] || code,
  }));

/** DEFAULT minimum age to register on DTS (admin can override via minAge). */
export const MIN_AGE = 21;

/** Effective minimum registration age (admin value if valid, else default 21). */
export const getMinAge = () => {
  const admin = Number(_complianceCfg.minAge);
  return Number.isInteger(admin) && admin >= 18 && admin <= 99 ? admin : MIN_AGE;
};

/** Message shown when access is blocked for an excluded jurisdiction. */
export const EXCLUDED_JURISDICTION_MESSAGE =
  "Excluded Jurisdiction: Void Where Prohibited.";

/** True if the given state code is an excluded jurisdiction (admin list or default). */
export const isExcludedState = (code) =>
  !!code && getExcludedStateCodes().includes(String(code).toUpperCase());

/**
 * Whole years old as of `now`, from a date of birth (Date, timestamp, or string).
 * Returns null if the DOB can't be parsed. Boundary: someone turning 21 today is 21.
 */
export const ageFromDob = (dob, now = new Date()) => {
  if (dob === null || dob === undefined || dob === "") return null;
  const d = dob instanceof Date ? dob : new Date(typeof dob === "number" ? dob : String(dob));
  if (isNaN(d.getTime())) return null;
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
};
